/**
 * SplashScreen — full-viewport brand splash for app boot + step
 * transitions.
 *
 * Replaces ad-hoc loading overlays (e.g. AuthGate's inline spinner
 * card). Composes Logo + RingsLoader + ProgressBar + brand copy on a
 * token-driven ambient gradient.
 *
 * Two variants via the strategy pattern:
 *   - `boot`: indeterminate progress bar; one big "Crafting your
 *     learning path"-style headline; used for cold session check.
 *   - `step`: explicit "Step X of Y" counter; determinate progress
 *     bar driven by `progress`; optional skip button.
 *
 * ARIA: role="status" + aria-live="polite" so SR announces title
 * changes during boot. Honours prefers-reduced-motion via the
 * composed RingsLoader.
 */

import type { ReactNode } from 'react';
import Logo from './Logo';
import RingsLoader from './RingsLoader';
import ProgressBar from './ProgressBar';
import './SplashScreen.css';

export type SplashScreenVariant = 'boot' | 'step';

export type SplashScreenProps = {
    variant: SplashScreenVariant;
    /** Primary heading (display font). */
    title: string;
    /** Optional supporting line. */
    subtitle?: ReactNode;
    /** When variant='step': current step (1-indexed). */
    step?: number;
    /** When variant='step': total step count. */
    totalSteps?: number;
    /** 0..100 — switches the progress bar to determinate. Indeterminate
     *  when omitted. */
    progress?: number;
    /** Optional skip / cancel CTA — rendered low-emphasis at the bottom. */
    onSkip?: () => void;
    /** Skip button label. Default 'Skip'. */
    skipLabel?: string;
    className?: string;
};

function SplashScreen({
    variant,
    title,
    subtitle,
    step,
    totalSteps,
    progress,
    onSkip,
    skipLabel = 'Skip',
    className = '',
}: SplashScreenProps) {
    return (
        <section
            className={`ui-splash ui-splash--${variant} ${className}`.trim()}
            role="status"
            aria-live="polite"
        >
            <div className="ui-splash__panel">
                <Logo size="lg" wordmark />

                <RingsLoader size="lg" tone="brand" label={title} />

                <div className="ui-splash__copy">
                    <h1 className="ui-splash__title">{title}</h1>
                    {subtitle && <p className="ui-splash__subtitle">{subtitle}</p>}
                </div>

                {variant === 'step' && step != null && totalSteps != null && (
                    <p className="ui-splash__steps" aria-label={`Step ${step} of ${totalSteps}`}>
                        Step {step} of {totalSteps}
                    </p>
                )}

                <div className="ui-splash__progress">
                    <ProgressBar
                        value={progress}
                        indeterminate={typeof progress !== 'number'}
                        showLabel={typeof progress === 'number'}
                        ariaLabel="Loading progress"
                    />
                </div>

                {onSkip && (
                    <button
                        type="button"
                        className="ui-splash__skip"
                        onClick={onSkip}
                    >
                        {skipLabel}
                    </button>
                )}
            </div>
        </section>
    );
}

export default SplashScreen;
